import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Users, BarChart3, FileText, Headphones, Mail, Zap, FolderOpen, PenTool, Database, Calendar } from 'lucide-react';

const ZohoOne = () => {
  const zohoApps = [
    {
      icon: <Users className="w-8 h-8 text-blue-700" />,
      name: 'Zoho CRM',
      description: 'Compleet systeem voor leadbeheer, salesprocessen, klantgegevens, sales pipelines, geautomatiseerde workflows en uitgebreide rapportages. Ideaal voor het structureren van uw verkoopproces.',
      link: '/zoho-crm',
    },
    {
      icon: <FileText className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Books',
      description: 'Professionele boekhoudsoftware voor offertes, facturen, betalingsbeheer en financiële administratie. Volledig geïntegreerd met uw CRM voor naadloze facturatie vanuit sales.',
      link: '/zoho-books',
    },
    {
      icon: <Calendar className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Projects',
      description: 'Krachtige projectmanagement tool voor taken, mijlpalen, Gantt charts, urenregistratie en team samenwerking. Beheer al uw projecten vanaf planning tot oplevering.',
      link: '/zoho-projects',
    },
    {
      icon: <Headphones className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Desk',
      description: 'Professionele helpdesk software voor ticketbeheer, klantenservice, supportprocessen en SLA-management. Verbeter uw klanttevredenheid met efficiënte afhandeling.',
      link: '/zoho-desk',
    },
    {
      icon: <Mail className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Campaigns',
      description: 'Complete e-mailmarketing oplossing voor nieuwsbrieven, campagnes, segmentatie en lead nurturing. Automatiseer uw marketingcommunicatie en meet resultaten.',
      link: '/contact',
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Flow',
      description: 'Krachtige automatiseringstool om Zoho-apps en externe software te koppelen. Automatiseer repetitieve taken en synchroniseer data tussen verschillende systemen.',
      link: '/contact',
    },
    {
      icon: <Database className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Forms',
      description: 'Flexibel formulierenbouwer voor intakeformulieren, leadformulieren, aanvragen en dataverzameling. Verzamel gestructureerd informatie en koppel direct aan uw CRM.',
      link: '/contact',
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Analytics',
      description: 'Geavanceerde business intelligence voor dashboards, rapportages, KPI-tracking en managementinformatie. Krijg realtime inzicht in alle bedrijfsdata.',
      link: '/contact',
    },
    {
      icon: <PenTool className="w-8 h-8 text-blue-700" />,
      name: 'Zoho Sign',
      description: 'Digitale ondertekeningsoftware voor offertes, contracten en juridische documenten. Versnelt uw salesproces en voldoet aan alle wettelijke eisen.',
      link: '/contact',
    },
    {
      icon: <FolderOpen className="w-8 h-8 text-blue-700" />,
      name: 'Zoho WorkDrive',
      description: 'Veilige cloud storage voor documentopslag, samenwerking en centrale dossiervorming. Deel bestanden veilig met collega\'s en klanten.',
      link: '/contact',
    },
    {
      icon: <Users className="w-8 h-8 text-blue-700" />,
      name: 'Zoho People',
      description: 'HR-managementsysteem voor personeelsbeheer, verlofadministratie, medewerkersgegevens en onboarding. Digitaliseer uw volledige HR-proces.',
      link: '/zoho-people',
    },
  ];

  const implementationSteps = [
    {
      step: '1',
      title: 'Analyse van de Bedrijfsprocessen',
      description: 'Wij starten met een grondige analyse van uw huidige werkwijze, bottlenecks en wensen.',
    },
    {
      step: '2',
      title: 'Keuze van de Juiste Zoho-apps',
      description: 'Op basis van uw processen adviseren wij welke apps u echt nodig heeft en welke niet.',
    },
    {
      step: '3',
      title: 'Inrichting van CRM, Workflows en Rechten',
      description: 'Wij configureren de apps volgens uw bedrijfsprocessen en richten gebruikersrechten in.',
    },
    {
      step: '4',
      title: 'Datamigratie en Koppelingen',
      description: 'Veilige migratie van bestaande data en het realiseren van koppelingen tussen apps.',
    },
    {
      step: '5',
      title: 'Automatisering met Zoho Flow',
      description: 'Wij bouwen slimme automatiseringen die handmatig werk elimineren en fouten voorkomen.',
    },
    {
      step: '6',
      title: 'Training en Livegang',
      description: 'Uw team wordt grondig getraind en wij begeleiden de lancering van het nieuwe systeem.',
    },
    {
      step: '7',
      title: 'Doorontwikkeling en Optimalisatie',
      description: 'Ook na go-live blijven wij betrokken met support en continue verbetering.',
    },
  ];

  const faqs = [
    {
      question: 'Wat is Zoho One?',
      answer: 'Zoho One is een complete business suite met meer dan 50 bedrijfsapplicaties voor CRM, sales, marketing, finance, projectmanagement, HR, samenwerking en meer. In plaats van losse softwarepakketten te kopen, krijgt u toegang tot het volledige Zoho ecosysteem voor één vaste prijs per gebruiker per maand. Dit maakt Zoho One ideaal voor bedrijven die willen professionaliseren zonder hoge kosten.',
    },
    {
      question: 'Welke apps zitten er in Zoho One?',
      answer: 'Zoho One bevat o.a. Zoho CRM (klantbeheer), Zoho Books (boekhouding), Zoho Projects (projectmanagement), Zoho Desk (klantenservice), Zoho Campaigns (e-mailmarketing), Zoho Analytics (rapportages), Zoho Sign (digitaal ondertekenen), Zoho WorkDrive (documentopslag), Zoho People (HR), Zoho Flow (automatisering) en nog 40+ andere apps. Alle apps zijn volledig geïntegreerd en delen data met elkaar.',
    },
    {
      question: 'Is Zoho One geschikt voor kleine bedrijven?',
      answer: 'Ja, Zoho One is zeer geschikt voor kleine tot middelgrote bedrijven. Door de All Employee pricing optie betaalt u slechts €37 per gebruiker per maand (excl. BTW) voor toegang tot alle 50+ apps. Dit is vaak voordeliger dan losse softwarepakketten kopen. Kleine bedrijven kunnen klein starten met enkele apps en later uitbreiden zonder extra licentiekosten.',
    },
    {
      question: 'Kan Guijs Consultancy Zoho One voor ons inrichten?',
      answer: 'Ja, als gecertificeerd Zoho Partner helpen wij Nederlandse bedrijven met de volledige implementatie van Zoho One. Wij analyseren eerst uw bedrijfsprocessen, adviseren welke apps u nodig heeft, richten deze in volgens uw werkwijze, migreren uw data, bouwen automatiseringen en trainen uw team. Zo haalt u maximaal rendement uit uw Zoho One investering.',
    },
    {
      question: 'Kunnen bestaande gegevens worden overgezet naar Zoho One?',
      answer: 'Ja, wij kunnen uw bestaande klantgegevens, contacten, projecten, facturen en andere data migreren vanuit Excel, andere CRM-systemen, boekhoudpakketten of oude databases naar Zoho One. Wij zorgen voor een veilige en correcte datamigratie waarbij u niets kwijtraakt. Ook helpen wij met het opschonen en structureren van data voor een optimale start.',
    },
    {
      question: 'Kan Zoho One gekoppeld worden aan andere software?',
      answer: 'Ja, Zoho One heeft standaard integraties met 1000+ externe tools zoals Google Workspace, Microsoft 365, Mailchimp, Slack en meer. Daarnaast kunt u met Zoho Flow (onderdeel van Zoho One) zelf koppelingen bouwen tussen Zoho en externe systemen. Ook maatwerk API-koppelingen zijn mogelijk. Wij helpen u graag met het realiseren van deze integraties.',
    },
    {
      question: 'Hoe lang duurt een Zoho One implementatie?',
      answer: 'Dit hangt af van de complexiteit van uw processen en het aantal apps dat u wilt gebruiken. Een basis CRM implementatie kan binnen 2-4 weken live zijn. Een complete Zoho One omgeving met meerdere apps, automatiseringen en integraties duurt meestal 6-12 weken. Wij werken in fases, zodat u al snel met de eerste apps kunt starten terwijl wij doorwerken aan de volgende stap.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              50+ Bedrijfsapplicaties in Één Platform
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Zoho One Implementatie voor Bedrijven
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Zoho One is de complete business suite met meer dan 50 geïntegreerde applicaties voor CRM, sales, 
              marketing, support, finance, projectmanagement, HR en samenwerking. Alles wat uw bedrijf nodig heeft 
              in één platform - voor slechts €37 per gebruiker per maand.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  Plan een Vrijblijvend Adviesgesprek
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
              
              <a href="tel:0638823471">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  Bel: 06 3882 3471
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="flex items-center justify-center space-x-3 text-gray-700">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="font-medium">Gecertificeerd Zoho Partner</span>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-700">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="font-medium">Volledige Implementatie</span>
              </div>
              <div className="flex items-center justify-center space-x-3 text-gray-700">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="font-medium">Continue Ondersteuning</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Kiezen voor Zoho One?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                De meeste bedrijven gebruiken meerdere losse softwarepakketten: een CRM voor klantbeheer, een apart 
                boekhoudprogramma, een projectmanagement tool, een helpdesk systeem en nog meer. Dit leidt tot 
                versnippering van data, dubbele invoer en inefficiënte processen.
              </p>
              <p>
                Zoho One lost dit probleem op door alles in één geïntegreerd platform te combineren. Van 
                leadgeneratie tot facturatie, van projectmanagement tot HR - alle data en processen zijn 
                met elkaar verbonden. Dit betekent: één waarheid, geen dubbele invoer en complete automatisering.
              </p>
              <p>
                <strong>Guijs Consultancy helpt u met:</strong>
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Advies en keuze:</strong> Welke Zoho One apps heeft uw bedrijf echt nodig?</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Inrichting en configuratie:</strong> Apps instellen volgens uw bedrijfsprocessen</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Koppelingen tussen apps:</strong> Naadloze data-uitwisseling binnen Zoho One</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Automatisering:</strong> Workflows en processen die handmatig werk elimineren</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Datamigratie:</strong> Veilige overzet van bestaande klant- en projectdata</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Training en adoptie:</strong> Uw team leert werken met de nieuwe systemen</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Optimalisatie:</strong> Continue verbetering van uw Zoho One omgeving</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Zoho One Apps Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              De Belangrijkste Zoho One Apps
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Zoho One bevat meer dan 50 applicaties. Dit zijn de meest gebruikte apps die wij implementeren 
              voor Nederlandse bedrijven.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {zohoApps.map((app, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="mb-4">{app.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{app.name}</h3>
                  <p className="text-gray-600 mb-4">{app.description}</p>
                  <Link 
                    to={app.link}
                    className="text-blue-700 hover:text-blue-800 font-semibold inline-flex items-center group"
                  >
                    Meer informatie
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Professional Implementation Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Waarom Zoho One Laten Implementeren door Guijs Consultancy?
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              Veel bedrijven beginnen enthousiast met Zoho One, maar gebruiken uiteindelijk maar een fractie 
              van de mogelijkheden. Een professionele implementatie maakt het verschil.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Zonder Professionele Inrichting</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start space-x-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <span>Medewerkers gebruiken slechts een paar apps</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <span>Data wordt dubbel ingevoerd in verschillende systemen</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <span>Geen automatisering, veel handmatig werk</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <span>Onduidelijke processen en versnipperde informatie</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <span>Frustratie bij het team over complexiteit</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Met Professionele Implementatie</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Systeem ingericht volgens uw specifieke werkprocessen</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Alle apps naadloos met elkaar verbonden</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Slimme automatiseringen die tijd en fouten besparen</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Duidelijke workflows die iedereen begrijpt</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Team werkt met plezier in het nieuwe systeem</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Onze Aanpak</h3>
              <p className="text-gray-700 mb-4">
                Bij Guijs Consultancy kijken wij eerst naar <strong>uw bedrijfsproces</strong> en richten daarna 
                de software logisch in. Niet andersom. Dit betekent dat het systeem aansluit op hoe u werkt, 
                in plaats van dat u moet veranderen voor de software.
              </p>
              <p className="text-gray-700">
                Wij focussen op <strong>praktische oplossingen</strong> die uw team echt gaat gebruiken. 
                Door slimme automatisering bespaart u tijd en voorkomt u fouten. En na de implementatie 
                blijven wij betrokken voor doorontwikkeling en optimalisatie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Voor Welke Bedrijven is Zoho One Geschikt?
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              Zoho One is ideaal voor Nederlandse MKB-bedrijven die willen professionaliseren en groeien.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Zakelijke dienstverleners (consultants, adviseurs, bureaus)',
                'Vastgoedbedrijven en property managers',
                'Incassobedrijven en debiteurenbeheerders',
                'Projectorganisaties en bouwbedrijven',
                'IT-bedrijven en softwareleveranciers',
                'Handelsbedrijven met webshops',
                'Bedrijven met 5-500 medewerkers',
                'Organisaties die willen overstappen van losse tools naar één platform',
                'Bedrijven die handmatige processen willen automatiseren',
                'Organisaties die beter inzicht willen in hun data',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-4 bg-white rounded-lg shadow">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-lg text-gray-700 mb-6">
                Herkent u uw organisatie hierin? Dan is Zoho One waarschijnlijk een perfecte match.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Bespreek uw Situatie
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Steps Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Zoho One Inrichting in Fases
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wij volgen een bewezen stappenplan voor een succesvolle Zoho One implementatie.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {implementationSteps.map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-700 text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-lg text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              Veelgestelde Vragen over Zoho One
            </h2>
            <p className="text-xl text-gray-600 mb-12 text-center">
              De meest gestelde vragen over Zoho One implementatie en inrichting.
            </p>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={`item-${index}`} value={`item-${index}`} className="bg-white border-2 border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-blue-700 py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <CTASection 
        title="Wil je weten welke Zoho One apps jouw bedrijf echt nodig heeft?"
        subtitle="Plan een vrijblijvend adviesgesprek met Guijs Consultancy en ontdek hoe Zoho One uw processen kan transformeren."
        primaryButtonText="Neem Contact Op"
      />
    </div>
  );
};

export default ZohoOne;
